
import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useAuth, useUser, useClerk } from '@clerk/clerk-react';
import { BookOpen, FileText, StickyNote, Upload, Plus, User, Search, Settings, HelpCircle, ChevronLeft, ChevronDown, ChevronRight, Minus, Loader2, Trash2, LogOut, Brain, Layers } from 'lucide-react';
import { motion } from 'motion/react';
import { useSwipeClickGuard } from './use-swipe-click-guard';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Progress } from './ui/progress';
import SourcityLogo from '../../imports/SourcityLogoWordmarkIconEmbedded1';
import { useLibraryStore } from '../../stores/libraryStore';
import { useNotesStore } from '../../stores/notesStore';
import { useQuizStore } from '../../stores/quizStore';
import { useUserStore } from '../../stores/userStore';
import { UpgradeModal, useUpgradeCheck, LimitType } from './upgrade-modal';
import { toast } from 'sonner';
import { LanguageToggle } from './language-toggle';
import { useI18n, useLocaleFormat } from '../../i18n';

type TabType = 'library' | 'quiz' | 'notes';

interface SidebarProps {
  contentOnly?: boolean;
  initialCreateQuiz?: boolean;
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  sidebarWidth: number;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  onShowProfile?: () => void;
  onShowSettings?: () => void;
  onShowHelp?: () => void;
}

export function Sidebar({ contentOnly = false, initialCreateQuiz = false, activeTab, onTabChange, sidebarWidth, isCollapsed = false, onToggleCollapse, onShowProfile, onShowSettings, onShowHelp }: SidebarProps) {
  const { t } = useI18n();
  const fmt = useLocaleFormat();
  const { getToken, userId, isLoaded } = useAuth();
  const { user } = useUser();
  const { signOut } = useClerk();
  
  const { 
    materials = [], 
    selectedMaterial, 
    selectMaterial, 
    uploadFile, 
    pollUploadStatus,
    deleteMaterial,
    fetchLibrary,
    uploadProgress,
    isLoading: libraryLoading 
  } = useLibraryStore();
  
  const { notes = [], currentDocumentNotes = [], searchQuery, setSearchQuery } = useNotesStore();
  const { generateQuiz, isGenerating, hasGenerated, savedChallenges, activeChallengeId, loadChallenge, deleteChallenge,
    challengeOwner, challengeAccess, challengeAccessLoading, challengeAccessError,
    setChallengeOwner, refreshChallengeAccess, reopenTrial } = useQuizStore();
  const ownerRef = React.useRef(userId);
  ownerRef.current = userId;
  const access = challengeOwner === userId ? challengeAccess : null;
  const checkingAccess = challengeAccessLoading || (!access && !challengeAccessError);
  const canGenerate = !!access && !challengeAccessLoading && (access.paid || access.trial.status === 'available');
  const paidChallenges = access?.paid === true;
  const maxQuestions = access?.maxQuestions || 3;
  const [challengeError, setChallengeError] = useState<string | null>(null);
  const refreshAccess = useCallback(() => {
    if (userId) return refreshChallengeAccess(userId, getToken);
  }, [userId, getToken, refreshChallengeAccess]);

  const controlId = React.useId();
  const generationPendingRef = React.useRef(false);
  const mountedRef = React.useRef(true);
  const [isSubmittingQuiz, setIsSubmittingQuiz] = useState(false);
  const [showCreateQuiz, setShowCreateQuiz] = useState(initialCreateQuiz);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showTabMenu, setShowTabMenu] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [windowWidth, setWindowWidth] = useState(1920);
  const [expandedChallengeId, setExpandedChallengeId] = useState<string | null>(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeLimitType, setUpgradeLimitType] = useState<LimitType>('pdf_count');
  const [upgradeFileSize, setUpgradeFileSize] = useState<number | undefined>(undefined);
  const { isAtLimit } = useUpgradeCheck();
  const { fetchLimits } = useUserStore();
  
  // Quiz configuration state
  const [numFlashcards, setNumFlashcards] = useState(10);
  const [numQuestions, setNumQuestions] = useState(5);
  const [useFlashcards, setUseFlashcards] = useState(true);
  const [useQuestions, setUseQuestions] = useState(true);
  const [quizTypes, setQuizTypes] = useState({
    comprehension: true,
    factual: false,
    analytical: false,
  });
  const [questionFormat, setQuestionFormat] = useState('single-answer');
  
  React.useEffect(() => {
    if (!isLoaded) return;
    setChallengeOwner(userId || null);
    generationPendingRef.current = false;
    setIsSubmittingQuiz(false);
    setChallengeError(null);
  }, [isLoaded, userId, setChallengeOwner]);
  React.useEffect(() => {
    if (isLoaded && userId && activeTab === 'quiz') void refreshAccess();
  }, [isLoaded, userId, activeTab, refreshAccess]);

  // Fetch library on mount
  React.useEffect(() => {
    if (contentOnly) return;
    const load = async () => {
      const token = await getToken();
      if (token) fetchLibrary(token);
    };
    load();
  }, [getToken, fetchLibrary, contentOnly]);
  React.useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  
  // Handle window resize
  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // File upload handler
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    
    const file = acceptedFiles[0];
    const token = await getToken();
    if (!token) {
      toast.error(t('library.toast.signInToUpload'));
      return;
    }

    // Proactive limit checks
    const fileSizeMB = file.size / (1024 * 1024);
    if (isAtLimit('pdf_count')) {
      setUpgradeLimitType('pdf_count');
      setUpgradeFileSize(undefined);
      setUpgradeModalOpen(true);
      return;
    }
    if (isAtLimit('pdf_size', fileSizeMB)) {
      setUpgradeLimitType('pdf_size');
      setUpgradeFileSize(fileSizeMB);
      setUpgradeModalOpen(true);
      return;
    }

    setIsUploading(true);
    try {
      const sessionId = await uploadFile(file, token);
      toast.info(t('library.toast.processing'));
      
      const material = await pollUploadStatus(sessionId, getToken);
      if (material) {
        selectMaterial(material);
        toast.success(t('library.toast.uploaded'));
        // Refresh limits after successful upload
        fetchLimits(token);
      }
    } catch (error: any) {
      // Check if it's a limit error from the backend
      const backendLimit = error?.data?.limit;
      if (backendLimit === 'file_size') {
        setUpgradeLimitType('pdf_size');
        setUpgradeFileSize(fileSizeMB);
        setUpgradeModalOpen(true);
      } else if (
        backendLimit === 'document_count'
        || error?.message?.includes('limit')
        || error?.message?.includes('Free tier')
      ) {
        setUpgradeLimitType('pdf_count');
        setUpgradeFileSize(undefined);
        setUpgradeModalOpen(true);
      } else {
        toast.error(t('library.toast.uploadFailedGeneric'));
      }
    } finally {
      setIsUploading(false);
    }
  }, [getToken, uploadFile, pollUploadStatus, selectMaterial, isAtLimit, fetchLimits, t]);
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-powerpoint': ['.ppt'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
    },
    maxFiles: 1,
    disabled: isUploading,
  });

  // Delete material handler
  const handleDeleteMaterial = async (materialId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const token = await getToken();
    if (!token) return;

    if (confirm(t('library.confirmDelete'))) {
      try {
        await deleteMaterial(materialId, token);
        toast.success(t('library.toast.deleted'));
      } catch (error) {
        toast.error(t('library.toast.deleteFailed'));
      }
    }
  };

  const approximateWidth = (sidebarWidth / 100) * windowWidth;
  const showIndividualTabs = approximateWidth > 140;
  const showLabels = approximateWidth > 200;

  // Filter notes based on search
  const filteredNotes = (notes || []).filter(note => 
    note.note_content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    note.selected_text?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-white/60 backdrop-blur-xl border-e border-white/20">
      {!contentOnly && <>
      {/* Collapse Button */}
      <div className="absolute top-2 end-2 z-10 shrink-0">
        <button
          aria-label={t("common.collapseSidebar")}
          onClick={onToggleCollapse}
          className="p-1.5 rounded-lg bg-white/40 hover:bg-white/60 transition-all active:scale-95"
        >
          <ChevronLeft className="h-4 w-4 text-[#434343] rtl-flip" />
        </button>
      </div>

      {/* Tab Buttons */}
      <div className="flex shrink-0 gap-1 p-3 border-b border-white/20 relative">
        {showIndividualTabs ? (
          <>
            <TabButton
              icon={<BookOpen className="h-5 w-5" />}
              label={t('nav.library')}
              active={activeTab === 'library'}
              onClick={() => onTabChange('library')}
              showLabel={showLabels}
            />
            <TabButton
              icon={<FileText className="h-5 w-5" />}
              label={t('nav.challenge')}
              active={activeTab === 'quiz'}
              onClick={() => onTabChange('quiz')}
              showLabel={showLabels}
            />
            <TabButton
              icon={<StickyNote className="h-5 w-5" />}
              label={t('nav.notes')}
              active={activeTab === 'notes'}
              onClick={() => onTabChange('notes')}
              showLabel={showLabels}
            />
          </>
        ) : (
          <>
            <button
              onClick={() => setShowTabMenu(!showTabMenu)}
              className="flex flex-1 items-center justify-center rounded-xl px-3 py-2.5 bg-white/90 backdrop-blur-sm shadow-sm transition-all duration-200 hover:bg-white active:scale-95"
            >
              <div className="w-8 h-8">
                <SourcityLogo />
              </div>
            </button>
            
            {showTabMenu && (
              <div className="absolute top-full start-3 end-3 mt-2 bg-white/90 backdrop-blur-xl border border-white/40 rounded-lg shadow-xl overflow-hidden z-20">
                {['library', 'quiz', 'notes'].map((tab) => (
                <button 
                    key={tab}
                    className="flex w-full items-center gap-3 px-4 py-3 hover:bg-white/60 transition-all text-start border-t border-white/20 first:border-t-0"
                  onClick={() => {
                      onTabChange(tab as TabType);
                    setShowTabMenu(false);
                  }}
                >
                    {tab === 'library' && <BookOpen className={`h-5 w-5 ${activeTab === 'library' ? 'text-[#7367FF]' : 'text-[#434343]'}`} />}
                    {tab === 'quiz' && <FileText className={`h-5 w-5 ${activeTab === 'quiz' ? 'text-[#7367FF]' : 'text-[#434343]'}`} />}
                    {tab === 'notes' && <StickyNote className={`h-5 w-5 ${activeTab === 'notes' ? 'text-[#7367FF]' : 'text-[#434343]'}`} />}
                    <span className={`text-sm ${activeTab === tab ? 'text-[#7367FF] font-medium' : 'text-[#434343]'}`}>
                      {tab === 'library' ? t('nav.library') : tab === 'quiz' ? t('nav.challenge') : t('nav.notes')}
                    </span>
                </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Content Area — min-h-0 required so flex child can shrink and scroll */}
      </>}
      <ScrollArea className="min-h-0 flex-1 px-2 py-3">
        <div className="space-y-3">
          {/* Library Tab */}
          {activeTab === 'library' && (
            <>
              {/* Upload Zone */}
              <div
                {...getRootProps()}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-4 text-start cursor-pointer transition-all duration-200 border-2 border-dashed ${
                  isDragActive 
                    ? 'border-[#7367FF] bg-[#7367FF]/10' 
                    : 'border-white/40 hover:border-[#7367FF]/50 hover:bg-white/80'
                } ${isUploading ? 'pointer-events-none opacity-50' : ''}`}
              >
                <input {...getInputProps()} />
                {isUploading ? (
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-[#7367FF] mb-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm">{t('common.uploading')}</span>
                    </div>
                    <Progress value={uploadProgress} className="h-1" />
                  </div>
                ) : (
                  <>
                    <Upload className="h-4 w-4 text-[#7367FF]" />
                    <span className="text-sm text-[#7367FF]">
                      {isDragActive ? t('library.dropFileHere') : t('library.upload')}
                    </span>
                  </>
                )}
              </div>

              {/* Library Items */}
              {libraryLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-[#7367FF]" />
                </div>
              ) : !Array.isArray(materials) || materials.length === 0 ? (
                <div className="text-center py-8 text-[#434343]/60 text-sm">
                  {t('library.empty')}
                </div>
              ) : (
                <div className="space-y-1">
                  {materials.map((item) => (
                    <SidebarSwipeableRow
                      key={item.id}
                      isActive={selectedMaterial?.id === item.id}
                      onSelect={() => {
                        selectMaterial(item);
                        if (selectedMaterial?.id !== item.id) {
                          toast.info(t('library.toast.opening', { name: item.original_filename || item.filename }));
                        }
                      }}
                      onDelete={async () => {
                        const token = await getToken();
                        if (!token) return;
                        if (confirm(t('library.confirmDelete'))) {
                          try {
                            await deleteMaterial(item.id, token);
                            toast.success(t('library.toast.deleted'));
                          } catch {
                            toast.error(t('library.toast.deleteFailed'));
                            return false;
                          }
                        }
                      }}
                    >
                      <FileText className={`h-4 w-4 flex-shrink-0 ${
                        selectedMaterial?.id === item.id ? 'text-[#7367FF]' : 'text-[#434343]/60'
                      }`} />
                      <span className={`text-sm flex-1 min-w-0 truncate ${
                        selectedMaterial?.id === item.id ? 'text-[#7367FF] font-medium' : 'text-[#434343]'
                      }`}>
                        {item.original_filename || item.filename}
                      </span>
                    </SidebarSwipeableRow>
                  ))}
                </div>
              )}
            </>
          )}

          {activeTab === 'quiz' && (
            <section aria-label={t('challenge.access.title')} className="space-y-2 rounded-xl border border-[#7367FF]/20 bg-[#7367FF]/5 p-3 text-sm text-[#434343]">
              <p role="status" aria-live="polite">
                {checkingAccess ? t('challenge.access.checking')
                  : challengeAccessError || !access ? t('challenge.access.error')
                  : paidChallenges ? t('challenge.access.paid')
                  : access.trial.status === 'available' ? t('challenge.access.available')
                  : access.trial.status === 'pending' ? t('challenge.access.pending')
                  : access.trial.status === 'recovery_required' ? t('challenge.access.recovery')
                  : t('challenge.access.used')}
              </p>
              {challengeError && <p role="alert">{t(challengeError)}</p>}
              {access?.trial.status === 'completed' && !access.trial.challenge && <p>{t('challenge.access.sourceUnavailable')}</p>}
              {access?.trial.challenge && !challengeAccessLoading && (
                <Button className="w-full whitespace-normal bg-[#7367FF] text-white hover:bg-[#5f56e6]" onClick={() => {
                  if (userId && reopenTrial(userId)) { setShowCreateQuiz(false); onTabChange('quiz'); }
                }}>{t('challenge.access.reopen')}</Button>
              )}
              {(challengeAccessError || access?.trial.status === 'pending' || access?.trial.status === 'recovery_required') && (
                <Button variant="outline" className="w-full whitespace-normal" disabled={checkingAccess} onClick={() => { setChallengeError(null); void refreshAccess(); }}>{t('challenge.access.retry')}</Button>
              )}
              {access && !paidChallenges && onShowProfile && (
                <Button variant="outline" className="w-full whitespace-normal" onClick={onShowProfile}>{t('challenge.access.upgrade')}</Button>
              )}
            </section>
          )}

          {/* Quiz Tab */}
          {activeTab === 'quiz' && !showCreateQuiz && (
            <>
              <ActionItem 
                icon={<Plus className="h-4 w-4" />} 
                label={t('challenge.createNew')} 
                onClick={() => setShowCreateQuiz(true)}
              />

              {/* Saved Challenges List */}
              {savedChallenges.length > 0 ? (
                <div className="space-y-1 mt-1">
                  {savedChallenges.map((challenge) => {
                    const isExpanded = expandedChallengeId === challenge.id;
                    const isActive = activeChallengeId === challenge.id;
                    return (
                      <div key={challenge.id}>
                        <SidebarSwipeableRow
                          isActive={isActive}
                          onSelect={() => {
                            loadChallenge(challenge.id);
                            onTabChange('quiz');
                          }}
                          onDelete={() => {
                            deleteChallenge(challenge.id);
                            toast.success(t('challenge.toast.deleted'));
                          }}
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedChallengeId(isExpanded ? null : challenge.id);
                            }}
                            className="flex-shrink-0 p-0.5 rounded hover:bg-white/60 transition-colors"
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-3.5 w-3.5 text-[#434343]/60" />
                            ) : (
                              <ChevronRight className="h-3.5 w-3.5 text-[#434343]/60" />
                            )}
                          </button>
                          <p className={`text-sm flex-1 min-w-0 truncate ${isActive ? 'text-[#7367FF] font-medium' : 'text-[#434343]'}`}>
                            {challenge.name}
                          </p>
                        </SidebarSwipeableRow>

                        {/* Expanded Content — individual items */}
                        {isExpanded && (
                          <div className="ms-6 me-1 mt-0.5 mb-1 space-y-0.5">
                            {challenge.flashcards.length > 0 && (
                              <>
                                <p className="text-[10px] uppercase tracking-wider text-[#434343]/40 px-2 pt-1">
                                  {t('challenge.flashcardsCount', { count: challenge.flashcards.length })}
                                </p>
                                {challenge.flashcards.map((fc, idx) => (
                                  <button
                                    key={`fc-${idx}`}
                                    onClick={() => {
                                      loadChallenge(challenge.id);
                                      useQuizStore.getState().setCurrentIndex(idx);
                                      onTabChange('quiz');
                                    }}
                                    className="flex items-start gap-2 w-full text-start rounded-md px-2 py-1.5 hover:bg-white/60 transition-all"
                                  >
                                    <Layers className="h-3 w-3 text-[#2DD2FE] flex-shrink-0 mt-0.5" />
                                    <span dir="auto" className="text-xs text-[#434343]/80 line-clamp-2">{fc.front}</span>
                                  </button>
                                ))}
                              </>
                            )}
                            {challenge.questions.length > 0 && (
                              <>
                                <p className="text-[10px] uppercase tracking-wider text-[#434343]/40 px-2 pt-1">
                                  {t('challenge.questionsCount', { count: challenge.questions.length })}
                                </p>
                                {challenge.questions.map((q, idx) => (
                                  <button
                                    key={`q-${idx}`}
                                    onClick={() => {
                                      loadChallenge(challenge.id);
                                      useQuizStore.getState().setCurrentIndex(challenge.flashcards.length + idx);
                                      onTabChange('quiz');
                                    }}
                                    className="flex items-start gap-2 w-full text-start rounded-md px-2 py-1.5 hover:bg-white/60 transition-all"
                                  >
                                    <Brain className="h-3 w-3 text-[#7367FF] flex-shrink-0 mt-0.5" />
                                    <span dir="auto" className="text-xs text-[#434343]/80 line-clamp-2">{q.question}</span>
                                  </button>
                                ))}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-[#434343]/60 text-sm">
                  {t('challenge.empty')}
                </div>
              )}
            </>
          )}

          {/* Quiz Configuration */}
          {activeTab === 'quiz' && showCreateQuiz && (
            <div className="space-y-4 px-1">
              <button 
                onClick={() => setShowCreateQuiz(false)}
                className="text-[#7367FF] text-sm hover:underline flex items-center gap-1"
              >
                <ChevronLeft className="h-4 w-4 rtl-flip" />
                {t('challenge.backToChallenges')}
              </button>
              
              {/* Flashcards & Questions */}
              <div className="space-y-3">
                {!paidChallenges && <p className="text-xs text-[#434343]/70">{t('challenge.access.cardsPaid')}</p>}
                <div className="flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 bg-white/40 backdrop-blur-sm">
                  <Label htmlFor={`${controlId}-flashcards-toggle`} className="text-sm text-[#434343]">{t('challenge.flashcards')}</Label>
                  <div className="flex items-center gap-2">
                    {paidChallenges && useFlashcards && (
                      <div className="flex items-center gap-1 bg-white/60 rounded-lg px-1">
                        <button aria-label={t('challenge.access.fewerCards')} disabled={!canGenerate || numFlashcards <= 1} onClick={() => setNumFlashcards(Math.max(1, numFlashcards - 1))} className="p-1 hover:bg-white/80 rounded">
                          <Minus className="h-3 w-3 text-[#7367FF]" />
                        </button>
                        <span className="text-sm text-[#434343] w-8 text-center">{numFlashcards}</span>
                        <button aria-label={t('challenge.access.moreCards')} disabled={!canGenerate || numFlashcards >= 50} onClick={() => setNumFlashcards(Math.min(50, numFlashcards + 1))} className="p-1 hover:bg-white/80 rounded">
                          <Plus className="h-3 w-3 text-[#7367FF]" />
                        </button>
                      </div>
                    )}
                    <Switch id={`${controlId}-flashcards-toggle`} checked={paidChallenges && useFlashcards} disabled={!canGenerate || !paidChallenges} onCheckedChange={setUseFlashcards} />
                  </div>
                </div>
                
                <div className="flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 bg-white/40 backdrop-blur-sm">
                  <Label htmlFor={`${controlId}-questions-toggle`} className="text-sm text-[#434343]">{t('challenge.questions')}</Label>
                  <div className="flex items-center gap-2">
                    {(!paidChallenges || useQuestions) && (
                      <div className="flex items-center gap-1 bg-white/60 rounded-lg px-1">
                        <button aria-label={t('challenge.access.fewerQuestions')} disabled={!canGenerate || Math.min(numQuestions, maxQuestions) <= 1} onClick={() => setNumQuestions(Math.max(1, Math.min(numQuestions, maxQuestions) - 1))} className="p-1 hover:bg-white/80 rounded">
                          <Minus className="h-3 w-3 text-[#7367FF]" />
                        </button>
                        <span className="text-sm text-[#434343] w-8 text-center">{Math.min(numQuestions, maxQuestions)}</span>
                        <button aria-label={t('challenge.access.moreQuestions')} disabled={!canGenerate || numQuestions >= maxQuestions} onClick={() => setNumQuestions(Math.min(maxQuestions, numQuestions + 1))} className="p-1 hover:bg-white/80 rounded">
                          <Plus className="h-3 w-3 text-[#7367FF]" />
                        </button>
                      </div>
                    )}
                    <Switch id={`${controlId}-questions-toggle`} checked={!paidChallenges || useQuestions} disabled={!canGenerate || !paidChallenges} onCheckedChange={setUseQuestions} />
                  </div>
                </div>
              </div>

              {/* Generate Button */}
              <Button 
                className="w-full bg-[#7367FF] hover:bg-[#5f56e6] text-white shadow-lg shadow-[#7367FF]/30"
                onClick={async () => {
                  if (!selectedMaterial || !canGenerate || !userId || generationPendingRef.current || useQuizStore.getState().isGenerating) return;
                  const owner = userId;
                  const current = () => mountedRef.current && ownerRef.current === owner;
                  // Must have at least one type enabled with count > 0
                  const willGenQ = !paidChallenges || (useQuestions && numQuestions > 0);
                  const willGenFC = paidChallenges && useFlashcards && numFlashcards > 0;
                  if (!willGenQ && !willGenFC) {
                    toast.error(t('challenge.toast.enableOneType'));
                    return;
                  }
                  generationPendingRef.current = true;
                  setIsSubmittingQuiz(true);
                  setChallengeError(null);
                  try {
                    const token = await getToken();
                    if (!current() || useQuizStore.getState().isGenerating) return;
                    if (!token) {
                      toast.error(t('challenge.toast.signInToGenerate'));
                      return;
                    }
                    toast.info(t('challenge.toast.generating'));
                    await generateQuiz(
                      selectedMaterial.id,
                      selectedMaterial.original_filename || selectedMaterial.filename,
                      token,
                      {
                        ownerId: owner,
                        numQuestions: Math.min(numQuestions, maxQuestions),
                        numFlashcards,
                        useQuestions: willGenQ,
                        useFlashcards: willGenFC,
                      }
                    );
                    if (!current()) return;
                    toast.success(t('challenge.toast.generated'));
                    setShowCreateQuiz(false);
                    onTabChange('quiz');
                  } catch (error: any) {
                    if (current()) {
                      const code = error?.data?.code;
                      const message = code === 'CHALLENGE_TRIAL_USED' || code === 'CHALLENGE_PAID_REQUIRED' ? 'challenge.access.upgradeRequired'
                        : code === 'CHALLENGE_TRIAL_BUSY' ? 'challenge.access.pending'
                        : code === 'CHALLENGE_TRIAL_RECOVERY_REQUIRED' ? 'challenge.access.recovery'
                        : code === 'CHALLENGE_SOURCE_UNAVAILABLE' ? 'challenge.access.sourceUnavailable'
                        : code === 'CHALLENGE_INVALID_INPUT' ? 'challenge.access.invalidCount'
                        : 'challenge.toast.generateFailed';
                      setChallengeError(message);
                      toast.error(t(message));
                      await refreshAccess();
                    }
                  } finally {
                    if (current()) {
                      generationPendingRef.current = false;
                      setIsSubmittingQuiz(false);
                    }
                  }
                }}
                disabled={!selectedMaterial || !canGenerate || isGenerating || isSubmittingQuiz}
              >
                {isGenerating || isSubmittingQuiz ? (
                  <>
                    <Loader2 className="h-4 w-4 me-2 animate-spin" />
                    {t('common.generating')}
                  </>
                ) : selectedMaterial ? t('common.generate') : t('challenge.selectDocumentFirst')}
              </Button>
            </div>
          )}

          {/* Notes Tab */}
          {activeTab === 'notes' && (
            <>
              <div className="relative px-1 mb-4 mt-2">
                <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#434343]" />
                <Input 
                  aria-label={t('notes.searchPlaceholder')}
                  placeholder={t('notes.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="ps-9 bg-white/60 backdrop-blur-sm border-white/40"
                />
              </div>

              {/* Current Document Notes */}
              {selectedMaterial && currentDocumentNotes && currentDocumentNotes.length > 0 && (
              <div className="space-y-1">
                <div className="px-2 py-1">
                  <p className="text-xs text-[#434343] opacity-60">{t('notes.fromCurrentMaterial')}</p>
                </div>
                  {currentDocumentNotes.map((note) => (
                    <div key={note.id} className="rounded-lg px-3 py-2.5 bg-white/40 hover:bg-white/60 transition-all">
                      <p dir="auto" className="text-sm text-[#434343] line-clamp-2">{note.note_content}</p>
                      {note.page_number && (
                        <p className="text-xs text-[#434343]/60 mt-1">{t('notes.pageLabel', { page: note.page_number })}</p>
                      )}
                    </div>
                ))}
              </div>
              )}

              {/* All Notes */}
              <div className="space-y-1 mt-4">
                <div className="px-2 py-1">
                  <p className="text-xs text-[#434343] opacity-60">{t('notes.allNotes')}</p>
                </div>
                {filteredNotes.length === 0 ? (
                  <div className="text-center py-8 text-[#434343]/60 text-sm">
                    {searchQuery ? t('notes.emptyFoundTitle') : t('notes.emptyTitle')}
                  </div>
                ) : (
                  filteredNotes.map((note) => (
                    <div key={note.id} className="rounded-lg px-3 py-2.5 bg-white/40 hover:bg-white/60 transition-all">
                      <p dir="auto" className="text-sm text-[#434343] line-clamp-2">{note.note_content}</p>
                      <p className="text-xs text-[#434343]/60 mt-1">
                        {note.document_title && `${note.document_title} • `}
                        {fmt.date(note.created_at)}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </ScrollArea>

      {!contentOnly && <>
      {/* Profile Button — z-50 so the account menu sits above library rows (SidebarSwipeableRow uses z-10) */}
      <div className="shrink-0 border-t border-white/20 bg-white/60 p-3 relative z-50">
        {showLabels && (
          <div className="mb-2 flex justify-center">
            <LanguageToggle variant="ghost" />
          </div>
        )}
        <button 
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 hover:bg-white/80 transition-all"
          aria-label={t("common.accountMenu")}
          aria-expanded={showProfileMenu}
          onClick={() => setShowProfileMenu(!showProfileMenu)}
        >
          <div className="w-8 h-8 flex items-center justify-center">
            <SourcityLogo />
          </div>
          <span className="text-sm text-[#434343] truncate">
            {user?.fullName || user?.firstName || user?.emailAddresses[0]?.emailAddress || t('common.profile')}
          </span>
        </button>
        
        {showProfileMenu && (
          <div className="absolute bottom-full start-3 end-3 z-50 mb-2 overflow-hidden rounded-lg border border-white/40 bg-white/90 shadow-xl backdrop-blur-xl">
            <button 
              className="flex w-full items-center gap-3 px-4 py-3 hover:bg-white/60 transition-all text-start"
              onClick={() => {
                setShowProfileMenu(false);
                onShowProfile?.();
              }}
            >
              <User className="h-4 w-4 text-[#7367FF]" />
              <span className="text-sm text-[#434343]">{t('common.profile')}</span>
            </button>
            <button 
              className="flex w-full items-center gap-3 px-4 py-3 hover:bg-white/60 transition-all text-start"
              onClick={() => {
                setShowProfileMenu(false);
                onShowSettings?.();
              }}
            >
              <Settings className="h-4 w-4 text-[#7367FF]" />
              <span className="text-sm text-[#434343]">{t('common.settings')}</span>
            </button>
            <button 
              className="flex w-full items-center gap-3 px-4 py-3 hover:bg-white/60 transition-all text-start"
              onClick={() => {
                setShowProfileMenu(false);
                onShowHelp?.();
              }}
            >
              <HelpCircle className="h-4 w-4 text-[#7367FF]" />
              <span className="text-sm text-[#434343]">{t('common.help')}</span>
            </button>
            <div className="border-t border-white/20" />
            <button 
              className="flex w-full items-center gap-3 px-4 py-3 hover:bg-red-50 transition-all text-start"
              onClick={async () => {
                setShowProfileMenu(false);
                await signOut();
                toast.success(t('profile.toast.signedOut'));
              }}
            >
              <LogOut className="h-4 w-4 text-red-500" />
              <span className="text-sm text-red-500">{t('common.signOut')}</span>
            </button>
          </div>
        )}
      </div>

      {/* Upgrade Modal */}
      </>}
      <UpgradeModal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        limitType={upgradeLimitType}
        attemptedSize={upgradeFileSize}
        onSecondaryAction={upgradeLimitType === 'pdf_count' ? () => {
          // Scroll to library to let user delete documents
          toast.info(t('library.toast.freeSpace'));
        } : undefined}
      />
    </div>
  );
}

interface TabButtonProps {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
  showLabel: boolean;
}

function TabButton({ icon, label, active, onClick, showLabel }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`flex flex-1 flex-col items-center justify-center gap-1 rounded-xl px-3 py-2.5 transition-all duration-200 ${
        active ? 'bg-white/90 backdrop-blur-sm shadow-sm' : 'text-[#434343] hover:bg-white/40'
      }`}
    >
      <div className={active ? 'text-[#7367FF]' : 'text-[#434343]'}>{icon}</div>
      {showLabel && (
        <span className={`text-[10px] ${active ? 'text-[#7367FF] font-medium' : 'text-[#434343]'}`}>
          {label}
        </span>
      )}
    </button>
  );
}

interface ActionItemProps {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
}

function ActionItem({ icon, label, onClick }: ActionItemProps) {
  return (
    <button 
      className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-start text-[#7367FF] hover:bg-white/80 transition-all" 
      onClick={onClick}
    >
      {icon}
      <span className="text-sm">{label}</span>
    </button>
  );
}

// Swipe-to-delete row for desktop sidebar (mirrors mobile context menu behavior)
interface SidebarSwipeableRowProps {
  children: React.ReactNode;
  isActive: boolean;
  onSelect: () => void;
  onDelete: () => void | boolean | Promise<void | boolean>;
}

function SidebarSwipeableRow({ children, isActive, onSelect, onDelete }: SidebarSwipeableRowProps) {
  const [open, setOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const swipeClickGuard = useSwipeClickGuard();
  const { isRTL, dir, t } = useI18n();
  // The action sits on the trailing edge, so the drag runs the other way in RTL.
  const openOffset = isRTL ? 74 : -74;

  return (
    <div dir={dir} className="relative overflow-hidden rounded-lg">
      {/* Delete button pinned to the trailing edge, only visible when card is dragged */}
      <div className="absolute end-0 top-0 bottom-0 w-[74px] bg-[#ff3b30] flex items-center justify-center z-0">
        <button
          disabled={isDeleting}
          onClick={async (e) => {
            e.stopPropagation();
            if (isDeleting) return;
            setIsDeleting(true);
            try {
              const result = await onDelete();
              if (result !== false) setOpen(false);
            } finally {
              setIsDeleting(false);
            }
          }}
          aria-label={t("common.delete")}
          className="flex items-center justify-center w-full h-full active:bg-[#e53429]"
        >
          <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
            {isDeleting ? <Loader2 className="h-4 w-4 text-white animate-spin" /> : <Trash2 className="h-4 w-4 text-white" />}
          </div>
        </button>
      </div>

      {/* Draggable foreground card */}
      <motion.div
        {...swipeClickGuard}
        drag="x"
        dragConstraints={isRTL ? { left: 0, right: 74 } : { left: -74, right: 0 }}
        dragElastic={isRTL ? { left: 0, right: 0.05 } : { left: 0.05, right: 0 }}
        dragMomentum={false}
        onDragEnd={(_event, info) => {
          setOpen(isRTL ? info.offset.x > 25 : info.offset.x < -25);
        }}
        animate={{ x: open ? openOffset : 0 }}
        transition={{ type: 'spring', stiffness: 500, damping: 40 }}
        className={`relative z-10 rounded-lg select-none cursor-grab active:cursor-grabbing ${
          isActive
            ? 'bg-[#eeedff] border border-[#7367FF]/30'
            : 'bg-white border border-gray-100 hover:bg-gray-50'
        }`}
        style={{ touchAction: 'pan-y', userSelect: 'none', WebkitUserSelect: 'none' as any }}
        onClick={() => {
          if (open) {
            setOpen(false);
          } else {
            onSelect();
          }
        }}
      >
        <div className="flex items-center gap-3 px-3 py-2.5">
          {children}
        </div>
      </motion.div>
    </div>
  );
}
